import type { UIMessage } from '@ai-sdk/react';
import { useMemo } from 'react';
import { LEGACY_CHAT_MESSAGE_ID_PREFIX } from '@bliks-io/contracts';
import { ChatMessages, type ChatTranscriptMessage } from '@/components/chat';
import { convertToMessageList } from '@/components/chat/utils';
import { useChatThinkingPhaseBuckets } from '@/lib/ai-thinking-phases';
import { BliksyIcon, type ChatFeedbackHandler } from '@bliks-io/ui';

// Hoisted: a fresh element per render would break `ChatMessage`'s memo and
// re-parse every message's markdown on every streamed token.
const ASSISTANT_ICON = <BliksyIcon size={28} />;

/**
 * Bliksy stores no message ids: a reloaded turn is read under a positional
 * `legacy:<generationId>:<index>` id, while a live-streamed one carries the
 * client-minted id the SDK gave it — an id no server-side write could ever
 * match. Votable therefore means "already canonical", which a stream-settle
 * refetch (`useBliksyChat`) delivers a moment after the answer finishes. An
 * answer whose stream errored never canonicalizes, so it never becomes votable.
 *
 * Deliberately narrower than `isReservedChatMessageId`: the other reserved
 * prefix, `chat-message:`, marks rows the normalizer could not identify — ids
 * that can collide, so a vote could land on the wrong row.
 *
 * Hoisted so the reference is stable across renders — `ChatMessage` is memoised.
 */
const canVoteBliksyMessage = (message: ChatTranscriptMessage): boolean =>
  message.id.startsWith(LEGACY_CHAT_MESSAGE_ID_PREFIX);

interface BliksyChatMessagesProps {
  messages: UIMessage[];
  /**
   * Org slug for entity-source chip links, threaded from the typed route.
   * Bliksy never emits inline `entity://` links, so this only powers the
   * MessageSources chip row (itself gated by VITE_USE_SOURCES_UI).
   */
  slug: string;
  isLoading?: boolean;
  isHistoryLoading?: boolean;
  userAvatar?: string;
  className?: string;
  /** Submits a thumbs verdict; MUST be referentially stable. */
  onFeedback?: ChatFeedbackHandler;
}

/**
 * Bliksy-branded chat messages container.
 * Delegates to the shared ChatMessages component with Bliksy icon.
 */
export function BliksyChatMessages({
  messages,
  slug,
  isLoading,
  isHistoryLoading,
  userAvatar,
  className,
  onFeedback
}: BliksyChatMessagesProps) {
  const chatMessages: ChatTranscriptMessage[] = useMemo(
    () => convertToMessageList(messages),
    [messages]
  );

  const thinkingPhaseBuckets = useChatThinkingPhaseBuckets();

  return (
    <ChatMessages
      messages={chatMessages}
      isLoading={isLoading}
      isHistoryLoading={isHistoryLoading}
      assistantIcon={ASSISTANT_ICON}
      userAvatar={userAvatar}
      thinkingPhaseBuckets={thinkingPhaseBuckets}
      slug={slug}
      className={className}
      onFeedback={onFeedback}
      canVoteMessage={canVoteBliksyMessage}
    />
  );
}
